import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { DispatchStatusBadge, DispatchStatusTracker } from '@/shared/components/DispatchStatusBadge';
import { AccountsSalesInvoice } from '../types';
import { Card, CardHeader, CardBody } from '@/shared/components/ui/Card';
import { Button } from '@/shared/components/ui/Button';
import { Modal } from '@/shared/components/ui/Modal';
import { Printer, FileDown, Mail, MessageCircle, Copy, XCircle, ArrowLeft, Truck } from 'lucide-react';
import { api, getApiErrorMessage } from '@/shared/api/axios';
import { Skeleton } from '@/shared/components/ui/Skeleton';
import { useQueryClient } from '@tanstack/react-query';
import { formatINR } from '@/shared/utils/currency';
import { Badge } from '@/shared/components/ui/Badge';
import toast from 'react-hot-toast';
import { cn } from '@/shared/utils/utils';
import { useUser } from '@/shared/hooks/useAuth';

interface Props {
  invoice: AccountsSalesInvoice;
  onBack: () => void;
  onDuplicate: (data: any) => void;
  onEdit: (data: any) => void;
}

/** Ledger figure cells: monospaced, fixed-width digits, right-aligned. */
const FIGURE_CELL = 'px-3 py-2 text-right font-mono tabular-nums';

export function InvoiceDetail({ invoice, onBack, onDuplicate, onEdit }: Props) {
  const qc = useQueryClient();
  const [notes, setNotes] = useState(invoice.notes || '');
  const [isSavingNotes, setIsSavingNotes] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  // Cancel & Zero is the Accounts team's (plus system admins) — same rule as
  // the server's requireInvoiceCancelUser and the Invoices list.
  const currentUser = useUser();
  const canCancelInvoice = ['superadmin', 'admin', 'owner'].includes(String(currentUser?.role || '').toLowerCase())
    || String(currentUser?.department || '').toLowerCase() === 'accounts';
  const [cancelReason, setCancelReason] = useState('');
  const [isCancelling, setIsCancelling] = useState(false);

  const customer = invoice.customerId as any;
  const isCancelled = invoice.status === 'cancelled';
  const isDraft = invoice.status === 'draft';
  const isReversed = invoice.status === 'reversed';

  const handleSaveNotes = async () => {
    setIsSavingNotes(true);
    try {
      await api.patch(`/api/accounts/sales-invoices/${invoice._id}/notes`, { notes });
      toast.success('Notes updated');
    } catch (err: any) { toast.error(err.response?.data?.error || 'Failed to save notes'); }
    setIsSavingNotes(false);
  };

  const handlePrint = () => {
    window.open(`/api/accounts/sales-invoices/${invoice._id}/pdf`, '_blank');
  };

  const handlePDF = () => {
    window.open(`/api/documents/invoice/${invoice._id}?format=pdf&download=1`, '_blank');
  };

  const handleEmail = async () => {
    try {
      const { data } = await api.post(`/api/accounts/sales-invoices/${invoice._id}/email`);
      toast.success(`Invoice emailed to ${data.to}`);
    } catch (err: any) { toast.error(err.response?.data?.error || 'Failed to send email'); }
  };

  const handleWhatsApp = () => {
    const custPhone = customer?.phone || '';
    const msg = encodeURIComponent(`Hi${customer?.name ? ' ' + customer.name : ''}, please find your invoice ${invoice.invoiceNumber} for ${formatINR(invoice.netTotal)}. Due: ${new Date(invoice.dueDate).toLocaleDateString('en-IN')}. Thank you!`);
    const url = custPhone
      ? `https://wa.me/${custPhone.replace(/[^0-9]/g, '')}?text=${msg}`
      : `https://wa.me/?text=${msg}`;
    window.open(url, '_blank');
  };

  const handleDuplicate = async () => {
    try {
      const { data } = await api.post(`/api/accounts/sales-invoices/${invoice._id}/duplicate`);
      onDuplicate(data);
    } catch (err: any) { toast.error(err.response?.data?.error || 'Failed to duplicate'); }
  };

  const handleFinalize = async () => {
    if (!confirm('Finalize this invoice? Once finalized, it cannot be edited (only notes). A ledger entry will be posted.')) return;
    try {
      await api.post(`/api/accounts/sales-invoices/${invoice._id}/finalize`, {}, { headers: { 'X-Idempotency-Key': crypto.randomUUID() } });
      toast.success('Invoice finalized and posted to ledger');
      qc.invalidateQueries({ queryKey: ['accounts', 'sales-invoices'] });
      onBack();
    } catch (err: any) { toast.error(err.response?.data?.error || 'Failed to finalize'); }
  };

  const handleReverse = async () => {
    const reason = prompt('Reason for reversal:');
    if (!reason) return;
    try {
      await api.post(`/api/accounts/sales-invoices/${invoice._id}/reverse`, { reason });
      toast.success('Invoice reversed');
      qc.invalidateQueries({ queryKey: ['accounts', 'sales-invoices'] });
      onBack();
    } catch (err: any) { toast.error(err.response?.data?.error || 'Failed to reverse'); }
  };

  const handleCancel = async () => {
    if (!cancelReason.trim()) {
      toast.error('Please enter a cancellation reason');
      return;
    }
    setIsCancelling(true);
    try {
      const { data } = await api.post(`/api/accounts/sales-invoices/${invoice._id}/cancel`, { reason: cancelReason.trim() });
      toast.success('Invoice cancelled and zeroed successfully');
      // What happened to its dispatch order — withdrawn, or the goods had already left.
      const dispatch = data?.dispatch as { withdrawn?: string[]; alreadyDispatched?: string[] } | undefined;
      if (dispatch?.withdrawn?.length) toast(`Removed from Dispatch: ${dispatch.withdrawn.join(', ')} (its challan cancelled too).`, { icon: '🚚', duration: 7000 });
      if (dispatch?.alreadyDispatched?.length) toast.error(`Goods for ${dispatch.alreadyDispatched.join(', ')} were already dispatched — arrange the return / credit note.`, { duration: 10000 });
      qc.invalidateQueries({ queryKey: ['accounts', 'sales-invoices'] });
      qc.invalidateQueries({ queryKey: ['invoice-requests'] });
      qc.invalidateQueries({ queryKey: ['invoices'] });
      setShowCancelModal(false);
      onBack();
    } catch (err: any) { toast.error(err.response?.data?.error || 'Failed to cancel'); }
    setIsCancelling(false);
  };

  // Status chips read as text, so they use the `-text` pair — the bright fills
  // (success/warning/danger) fail contrast at 1.99:1 when used as a glyph colour.
  const statusTones: Record<string, 'success' | 'warning' | 'danger' | 'neutral'> = {
    paid: 'success',
    partially_paid: 'warning',
    unpaid: 'danger',
    cancelled: 'neutral',
    reversed: 'neutral',
    draft: 'neutral',
  };

  const handleDeleteDraft = async () => {
    if (!window.confirm('Delete this draft invoice?')) return;
    try { await api.delete(`/api/accounts/sales-invoices/${invoice._id}`); qc.invalidateQueries({ queryKey: ['accounts', 'sales-invoices'] }); toast.success('Draft invoice deleted.'); onBack(); }
    catch (err: any) { toast.error(err.response?.data?.error || 'Failed to delete draft'); }
  };
  const statusTone = statusTones[invoice.status] ?? 'neutral';

  return (
    <div className="space-y-4">
      {/* Header + Actions */}
      <Card className="shadow-xs">
        <CardHeader
          title={
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="sm" onClick={onBack}><ArrowLeft className="h-4 w-4" /></Button>
              <div>
                <div className="text-lg font-semibold">{invoice.invoiceNumber}</div>
                <div className="text-sm text-muted-foreground">Generated on {new Date(invoice.createdAt || invoice.date).toLocaleDateString('en-IN')}</div>
              </div>
              <Badge tone={statusTone} className="ml-3">{invoice.status?.replace('_', ' ')}</Badge>
              {invoice.source === 'deal.won' && (
                <Badge tone="primary" className="ml-2 gap-1.5">
                  <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                  Auto-generated from Deal Won
                </Badge>
              )}
              {invoice.dispatchStatus && <DispatchStatusBadge status={invoice.dispatchStatus} className="ml-2" />}
            </div>
          }
          action={
            <div className="flex flex-wrap gap-2">
              {isDraft && <Button variant="primary" size="sm" onClick={handleFinalize}>Finalize & Post</Button>}
              {isDraft && <Button variant="outline" size="sm" onClick={() => onEdit({ editingId: invoice._id, __v: (invoice as any).__v, customerId: typeof invoice.customerId === 'string' ? invoice.customerId : (invoice.customerId as any)?._id, date: invoice.date, dueDate: invoice.dueDate, lineItems: invoice.lineItems, paymentTerms: invoice.paymentTerms, terms: invoice.terms, notes: invoice.notes })}>Edit</Button>}
              {isDraft && <Button variant="outline" size="sm" className="text-destructive" onClick={handleDeleteDraft}>Delete</Button>}
              {!isDraft && !isCancelled && !isReversed && <Button variant="outline" size="sm" onClick={handlePrint}><Printer className="h-4 w-4 mr-1" /> Print</Button>}
              {!isDraft && !isCancelled && !isReversed && <Button variant="outline" size="sm" onClick={handlePDF}><FileDown className="h-4 w-4 mr-1" /> PDF</Button>}
              {!isDraft && !isCancelled && !isReversed && <Button variant="outline" size="sm" onClick={handleEmail}><Mail className="h-4 w-4 mr-1" /> Email</Button>}
              {!isDraft && !isCancelled && !isReversed && <Button variant="outline" size="sm" onClick={handleWhatsApp}><MessageCircle className="h-4 w-4 mr-1" /> WhatsApp</Button>}
              <Button variant="outline" size="sm" onClick={handleDuplicate}><Copy className="h-4 w-4 mr-1" /> Duplicate</Button>
              {!isDraft && !isCancelled && !isReversed && <Button variant="outline" size="sm" onClick={handleReverse} className="text-orange-600 border-orange-300">Reverse</Button>}
              {!isCancelled && !isReversed && !isDraft && canCancelInvoice && <Button variant="outline" size="sm" onClick={() => setShowCancelModal(true)} className="text-destructive border-destructive/30"><XCircle className="h-4 w-4 mr-1" /> Cancel</Button>}
            </div>
          }
        />
      </Card>

      {/* Invoice Content */}
      <Card className="shadow-xs">
        <CardBody className="space-y-6">
          {/* Cancelled Banner */}
          {isCancelled && (
            <div className="rounded-lg border border-danger/30 bg-danger/10 p-3.5 text-xs text-danger-text space-y-1">
              <div className="flex items-center gap-2 font-bold text-sm">
                <XCircle className="h-4 w-4 text-danger" />
                <span>Invoice Cancelled / Zeroed</span>
              </div>
              <p>This invoice was cancelled and its balance zeroed out. Reversal entries were posted to the ledger.</p>
              {invoice.cancellationReason && (
                <p className="mt-1 font-medium"><strong className="text-foreground">Reason:</strong> {invoice.cancellationReason}</p>
              )}
              {invoice.cancelledAt && (
                <p className="text-[11px] text-muted-foreground">Cancelled on {new Date(invoice.cancelledAt).toLocaleDateString('en-IN')}</p>
              )}
            </div>
          )}

          {/* Dispatch & delivery — the same stages as Pending Dispatch (tracker #94). */}
          {invoice.dispatchStatus && (
            <div className="rounded-lg border border-border bg-muted/20 px-4 py-3">
              <div className="mb-2 flex flex-wrap items-center justify-between gap-2 text-xs">
                <span className="flex items-center gap-2 font-semibold text-foreground"><Truck className="h-4 w-4 text-primary" /> Dispatch</span>
                <span className="flex flex-wrap gap-x-4 gap-y-1 text-muted-foreground">
                  {invoice.dispatchStatus.challanNumber && <span>Challan <span className="font-mono text-foreground">{invoice.dispatchStatus.challanNumber}</span></span>}
                  {(invoice.dispatchStatus.transporter || invoice.dispatchStatus.courierName) && <span>Transporter <span className="text-foreground">{invoice.dispatchStatus.transporter || invoice.dispatchStatus.courierName}</span></span>}
                  {invoice.dispatchStatus.lrNumber && <span>LR <span className="font-mono text-foreground">{invoice.dispatchStatus.lrNumber}</span></span>}
                  {invoice.dispatchStatus.vehicleNumber && <span>Vehicle <span className="font-mono text-foreground">{invoice.dispatchStatus.vehicleNumber}</span></span>}
                </span>
              </div>
              <DispatchStatusTracker status={invoice.dispatchStatus} />
            </div>
          )}

          {/* Customer + Dates */}
          <div className="grid grid-cols-2 gap-6">
            <div>
              <p className="text-xs uppercase text-muted-foreground mb-1">Bill To</p>
              <p className="font-semibold">{customer?.name || 'Customer'}</p>
              {customer?.email && <p className="text-sm text-muted-foreground">{customer.email}</p>}
              {customer?.phone && <p className="text-sm text-muted-foreground">{customer.phone}</p>}
              {customer?.gstNumber && <p className="text-sm text-muted-foreground">GSTIN: {customer.gstNumber}</p>}
            </div>
            <div className="text-right space-y-1">
              <div><span className="text-xs uppercase text-muted-foreground">Invoice Date: </span><span className="font-medium">{new Date(invoice.date).toLocaleDateString('en-IN')}</span></div>
              <div><span className="text-xs uppercase text-muted-foreground">Due Date: </span><span className="font-medium">{new Date(invoice.dueDate).toLocaleDateString('en-IN')}</span></div>
              <div><span className="text-xs uppercase text-muted-foreground">Terms: </span><span>{invoice.paymentTerms || 'Net 30'}</span></div>
              <div><span className="text-xs uppercase text-muted-foreground">Amount Due: </span><span className={cn("font-mono tabular-nums text-data-lg font-semibold", isCancelled ? "text-danger-text font-bold" : "text-on-surface")}>{isCancelled ? '₹0' : (invoice.amountDue == null ? '—' : formatINR(invoice.amountDue))}</span></div>
            </div>
          </div>

          {/* Line Items Table — the printed tax-invoice convention: text left, every figure right and mono. */}
          <table className="w-full text-sm border-collapse">
            <thead className="bg-muted text-label-caps uppercase text-on-surface-variant">
              <tr>
                <th className="px-3 py-2.5 text-left w-8">#</th>
                <th className="px-3 py-2.5 text-left">Description</th>
                <th className="px-3 py-2.5 text-right">Qty</th>
                <th className="px-3 py-2.5 text-right">Rate</th>
                <th className="px-3 py-2.5 text-right">Disc.</th>
                <th className="px-3 py-2.5 text-right">GST</th>
                <th className="px-3 py-2.5 text-right">Amount</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {invoice.lineItems?.map((li, i) => (
                <tr key={i} className="hover:bg-muted/50">
                  <td className="px-3 py-2.5 font-mono tabular-nums text-muted-foreground">{i + 1}</td>
                  <td className="px-3 py-2.5">{li.description}{li.hsnSac && <span className="text-xs text-muted-foreground ml-2">({li.hsnSac})</span>}</td>
                  <td className={FIGURE_CELL}>{li.quantity ?? '—'}</td>
                  <td className={FIGURE_CELL}>{li.rate == null ? '—' : formatINR(li.rate)}</td>
                  <td className={FIGURE_CELL}>{li.discount ? formatINR(li.discount) : '—'}</td>
                  <td className="px-3 py-2.5 text-right font-mono tabular-nums">{(li.cgstRate || 0) + (li.sgstRate || 0) + (li.igstRate || 0)}%</td>
                  <td className={`${FIGURE_CELL} font-medium`}>{li.amount == null ? '—' : formatINR(li.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Totals: label/subtle figure, gross total pinned by a top hairline. */}
          <div className="flex justify-end">
            <div className="w-72 space-y-1 text-sm">
              <div className="flex justify-between"><span className="text-on-surface-variant">Sub Total</span><span className="font-mono tabular-nums">{invoice.subTotal == null ? '—' : formatINR(invoice.subTotal)}</span></div>
              {invoice.cgstTotal > 0 && <div className="flex justify-between text-muted-foreground"><span>CGST</span><span className="font-mono tabular-nums">{formatINR(invoice.cgstTotal)}</span></div>}
              {invoice.sgstTotal > 0 && <div className="flex justify-between text-muted-foreground"><span>SGST</span><span className="font-mono tabular-nums">{formatINR(invoice.sgstTotal)}</span></div>}
              {invoice.igstTotal > 0 && <div className="flex justify-between text-muted-foreground"><span>IGST</span><span className="font-mono tabular-nums">{formatINR(invoice.igstTotal)}</span></div>}
              {invoice.roundOff !== 0 && <div className="flex justify-between text-muted-foreground"><span>Round Off</span><span className="font-mono tabular-nums">{formatINR(invoice.roundOff)}</span></div>}
              <div className="mt-1 flex justify-between border-t border-border pt-2">
                <span className="text-label-sm uppercase tracking-wider text-on-surface-variant">Net Total</span>
                <span className="font-mono tabular-nums text-data-lg font-semibold text-on-surface">{invoice.netTotal == null ? '—' : formatINR(invoice.netTotal)}</span>
              </div>
            </div>
          </div>

          {/* Payment History */}
          {!isDraft && (
            <PaymentHistory invoiceId={invoice._id} />
          )}

          {/* Notes (editable) */}
          <div className="border-t pt-4">
            <label className="text-sm font-medium">Notes (editable)</label>
            <div className="flex gap-2 mt-1">
              <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2}
                className="flex-1 rounded-md border px-3 py-2 text-sm" placeholder="Add notes..." />
              <Button variant="secondary" size="sm" onClick={handleSaveNotes} disabled={isSavingNotes}>
                {isSavingNotes ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </div>

          <ExportDetails invoice={invoice} />

          {invoice.terms && (
            <div className="border-t pt-4">
              <label className="text-sm font-medium text-muted-foreground">Terms & Conditions</label>
              <p className="text-sm mt-1">{invoice.terms}</p>
            </div>
          )}
        </CardBody>
      </Card>

      {/* Cancel Modal */}
      <Modal open={showCancelModal} onClose={() => setShowCancelModal(false)} title="Cancel & Zero Invoice" size="sm" footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={() => setShowCancelModal(false)}>No, Keep It</Button>
          <Button variant="primary" onClick={handleCancel} disabled={isCancelling || !cancelReason.trim()} className="bg-destructive hover:bg-destructive/90">
            {isCancelling ? 'Cancelling & Zeroing...' : 'Yes, Cancel & Zero Invoice'}
          </Button>
        </div>
      }>
        <div className="py-4 space-y-3">
          <p className="text-sm">This will post a reversal entry to the ledger and mark the invoice as cancelled (₹0 balance due). This action cannot be undone.</p>
          <p className="text-sm text-destructive font-medium">Note: This only works if no payments have been recorded against this invoice.</p>
          <div>
            <label className="text-sm font-medium">Reason for cancellation <span className="text-destructive">*</span></label>
            <textarea value={cancelReason} onChange={e => setCancelReason(e.target.value)} rows={3}
              className="w-full rounded-md border px-3 py-2 text-sm mt-1" placeholder="e.g. Client cancelled order after finalization, payment not received" />
          </div>
        </div>
      </Modal>
    </div>
  );
}


// ── Export details (GSTR-1 Table 6A) ─────────────────────────────────────────
//
// Only shown once an invoice is marked as an export, so a domestic invoice —
// which is nearly all of them — is not cluttered with fields that do not apply.
//
// Editable after finalisation on purpose: the shipping bill usually does not
// exist when the invoice is raised, and Table 6A can be amended. Locking these
// with the rest of the invoice would make the refund unclaimable.
function ExportDetails({ invoice }: { invoice: AccountsSalesInvoice }) {
  const inv = invoice as any;
  const qc = useQueryClient();
  const [open, setOpen] = useState(Boolean(inv.isExport));
  const [form, setForm] = useState({
    isExport: Boolean(inv.isExport),
    exportType: inv.exportType || '',
    portCode: inv.portCode || '',
    shippingBillNumber: inv.shippingBillNumber || '',
    shippingBillDate: inv.shippingBillDate ? String(inv.shippingBillDate).slice(0, 10) : '',
  });
  const [saving, setSaving] = useState(false);
  const [issues, setIssues] = useState<string[]>([]);
  const [warnings, setWarnings] = useState<string[]>([]);

  const save = async (patch: Record<string, unknown>) => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/api/gstr/invoices/${invoice._id}/export-details`, patch);
      setIssues(data.issues || []);
      setWarnings(data.warnings || []);
      toast.success('Export details saved.');
      qc.invalidateQueries({ queryKey: ['accounts', 'sales-invoices'] });
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Could not save export details.');
    }
    setSaving(false);
  };

  if (!open) {
    return (
      <div className="border-t pt-4">
        <button onClick={() => setOpen(true)} className="text-sm font-medium text-primary">
          Mark as an export (GSTR-1 Table 6A)
        </button>
      </div>
    );
  }

  return (
    <div className="border-t pt-4 space-y-3">
      <label className="flex items-center gap-2 text-sm font-medium">
        <input type="checkbox" checked={form.isExport}
          onChange={(e) => { const v = e.target.checked; setForm({ ...form, isExport: v }); save({ isExport: v }); }} />
        This is an export
      </label>

      {form.isExport && (
        <>
          <div className="grid sm:grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs font-medium block mb-1">Export type</span>
              <select value={form.exportType}
                onChange={(e) => { setForm({ ...form, exportType: e.target.value }); save({ exportType: e.target.value }); }}
                className="w-full rounded-md border px-3 py-2 text-sm">
                <option value="">Select…</option>
                <option value="WPAY">WPAY — with payment of IGST</option>
                <option value="WOPAY">WOPAY — under LUT/bond, no IGST</option>
              </select>
            </label>
            <label className="block">
              <span className="text-xs font-medium block mb-1">Port code (ICEGATE)</span>
              <input value={form.portCode} maxLength={6} placeholder="INNSA1"
                onChange={(e) => setForm({ ...form, portCode: e.target.value.toUpperCase() })}
                onBlur={() => form.portCode !== (inv.portCode || '') && save({ portCode: form.portCode })}
                className="w-full rounded-md border px-3 py-2 text-sm font-mono" />
            </label>
            <label className="block">
              <span className="text-xs font-medium block mb-1">Shipping bill number</span>
              <input value={form.shippingBillNumber}
                onChange={(e) => setForm({ ...form, shippingBillNumber: e.target.value })}
                onBlur={() => form.shippingBillNumber !== (inv.shippingBillNumber || '') && save({ shippingBillNumber: form.shippingBillNumber })}
                className="w-full rounded-md border px-3 py-2 text-sm" />
            </label>
            <label className="block">
              <span className="text-xs font-medium block mb-1">Shipping bill date</span>
              <input type="date" value={form.shippingBillDate}
                onChange={(e) => { setForm({ ...form, shippingBillDate: e.target.value }); save({ shippingBillDate: e.target.value }); }}
                className="w-full rounded-md border px-3 py-2 text-sm" />
            </label>
          </div>

          <p className="text-xs text-muted-foreground">
            The election must match the tax actually charged — WOPAY with IGST on the invoice, or
            WPAY without it, is refused when the return is prepared.
          </p>

          {issues.length > 0 && (
            <div className="rounded-card-sm border border-danger/20 bg-danger/10 p-3 text-xs text-danger-text">
              <p className="mb-1 font-semibold">This invoice cannot be filed yet</p>
              <ul className="list-disc ml-4 space-y-0.5">{issues.map((i) => <li key={i}>{i}</li>)}</ul>
            </div>
          )}
          {warnings.length > 0 && (
            <div className="rounded-card-sm border border-warning/20 bg-warning/10 p-3 text-xs text-warning-text">
              <ul className="list-disc ml-4 space-y-0.5">{warnings.map((w) => <li key={w}>{w}</li>)}</ul>
            </div>
          )}
          {saving && <p className="text-xs text-muted-foreground">Saving…</p>}
        </>
      )}
    </div>
  );
}

// ── Payment History sub-component ────────────────────────────────────────────

function PaymentHistory({ invoiceId }: { invoiceId: string }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['accounts', 'invoice-payments', invoiceId],
    queryFn: async () => { const { data } = await api.get(`/api/accounts/sales-invoices/${invoiceId}/payments`); return data as { totalPaid: number; amountDue: number; grandTotal: number; payments: any[] }; },
    enabled: !!invoiceId,
  });

  // "Nothing has been paid" and "we could not read the payments" are different
  // answers; the second one must not be shown as the first.
  if (error) return (
    <div className="mt-4 border-t border-border pt-4">
      <p className="text-sm font-medium">Payment History</p>
      <p className="mt-1 rounded-card-sm border border-danger/20 bg-danger/10 px-3 py-2 text-body-sm text-danger-text">
        {getApiErrorMessage(error, 'Could not load the payment history for this invoice.')}
      </p>
    </div>
  );

  if (isLoading) return (
    <div className="mt-4 border-t border-border pt-4" aria-busy="true">
      <p className="text-sm font-medium">Payment History</p>
      <div className="mt-2 space-y-2">
        <Skeleton className="h-10 w-full rounded-lg" />
        <Skeleton className="h-10 w-full rounded-lg" />
      </div>
    </div>
  );

  if (!data || data.payments.length === 0) return (
    <div className="border-t border-border pt-4">
      <p className="text-sm font-medium">Payment History</p>
      <p className="text-sm text-muted-foreground">No payments recorded against this invoice yet. Record a payment to clear the outstanding amount.</p>
    </div>
  );

  return (
    <div className="border-t border-border pt-4">
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm font-medium">Payment History ({data.payments.length})</p>
        <div className="text-xs text-muted-foreground">
          Paid: <span className="font-mono tabular-nums font-medium text-success-text">{formatINR(data.totalPaid)}</span>
          {' / '}<span className="font-mono tabular-nums">{formatINR(data.grandTotal)}</span>
          {data.amountDue > 0 && <span className="ml-2 font-mono tabular-nums text-destructive">Due: {formatINR(data.amountDue)}</span>}
        </div>
      </div>
      <div className="space-y-2">
        {data.payments.map((p: any, i: number) => (
          <div key={p._id || i} className="flex items-center justify-between rounded-card-sm bg-muted/30 px-3 py-2 text-sm">
            <div>
              <span className="font-medium">{p.paymentNumber || `Payment ${i + 1}`}</span>
              <span className="ml-2 text-xs text-muted-foreground">{new Date(p.date).toLocaleDateString('en-IN')}</span>
              <span className="ml-2 px-1.5 py-0.5 rounded bg-muted text-xs capitalize">{p.paymentMethod || 'cash'}</span>
            </div>
            <span className="font-mono tabular-nums font-semibold text-success-text">{formatINR(p.amount)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
